/**
 * 画像アセットの存在確認とアイコン供給。
 *
 * 方針:
 *  ・実画像（PNG / WebP）が置かれていればそれを使う
 *  ・無ければ手続き生成の仮アイコンへ自動フォールバックする
 *  ・<img> で 404 を踏むとコンソールにエラーが出るため、
 *    fetch による事前プローブで「存在するものだけ」を読み込む
 */

import type { ScenePhase, StageDefinition, StageItemDefinition } from '../config/types';
import { createIconDataUrl, iconRenderSize } from './iconFactory';

/** Phaser のテクスチャキー（実画像）。 */
export function imageTextureKey(item: StageItemDefinition): string {
  return `item-image:${item.id}`;
}

/** Phaser のテクスチャキー（仮アイコン）。 */
export function placeholderTextureKey(item: StageItemDefinition): string {
  return `item-icon:${item.id}`;
}

/** Phaser のテクスチャキー（背景写真）。 */
export function sceneryPhotoKey(phase: ScenePhase): string {
  return `scenery-photo:${phase}`;
}

/** Phaser のテクスチャキー（背景ツアー写真）。 */
export function tourPhotoKey(sceneId: string): string {
  return `tour-photo:${sceneId}`;
}

export type ImageProbe = (url: string) => Promise<boolean>;

/** HEAD リクエストで画像の存在を確認する（失敗しても例外は投げない）。 */
export const fetchImageProbe: ImageProbe = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'force-cache' });
    if (!response.ok) return false;
    const contentType = response.headers.get('content-type') ?? '';
    // Vite の dev サーバは存在しないパスへ index.html を返すことがあるため除外
    if (contentType.includes('text/html')) return false;
    return true;
  } catch {
    return false;
  }
};

/**
 * 各アイテムの実画像が使えるかどうかを判定する。
 * enabled=false の場合は全て仮アイコンになる。
 */
export async function probeItemImages(
  items: readonly StageItemDefinition[],
  options: { enabled?: boolean; probe?: ImageProbe } = {},
): Promise<Map<string, boolean>> {
  const result = new Map<string, boolean>();
  const enabled = options.enabled ?? true;
  const probe = options.probe ?? fetchImageProbe;

  await Promise.all(
    items.map(async (item) => {
      if (!enabled || !item.image) {
        result.set(item.id, false);
        return;
      }
      result.set(item.id, await probe(item.image));
    }),
  );

  return result;
}

/**
 * 背景写真のうち、実際に配置されているものだけを抽出する。
 *
 * 1枚も無ければ空の Map が返り、背景は手続き生成の夜景のままになる。
 * 一部の時間帯だけ写真を置く運用も可能。
 */
export async function probeSceneryPhotos(
  stage: StageDefinition,
  options: { enabled?: boolean; probe?: ImageProbe } = {},
): Promise<Map<ScenePhase, string>> {
  const found = new Map<ScenePhase, string>();
  const enabled = options.enabled ?? true;
  const probe = options.probe ?? fetchImageProbe;
  const photos = stage.scenery?.photos;
  if (!enabled || !photos) return found;

  const entries = Object.entries(photos) as Array<[ScenePhase, string | undefined]>;
  await Promise.all(
    entries.map(async ([phase, url]) => {
      if (!url) return;
      if (await probe(url)) found.set(phase, url);
    }),
  );

  return found;
}

/**
 * 背景ツアー写真のうち、実際に配置されているものだけを抽出する。
 *
 * 1枚も無ければ空の Map が返り、背景は既存の手続き生成夜景／時間帯写真のままになる。
 */
export async function probeSceneryTour(
  stage: StageDefinition,
  options: { enabled?: boolean; probe?: ImageProbe } = {},
): Promise<Map<string, string>> {
  const found = new Map<string, string>();
  const enabled = options.enabled ?? true;
  const probe = options.probe ?? fetchImageProbe;
  const tour = stage.scenery?.tour;
  if (!enabled || !tour) return found;

  await Promise.all(
    tour.scenes.map(async (scene) => {
      if (await probe(scene.image)) found.set(scene.id, scene.image);
    }),
  );

  return found;
}

/**
 * DOM（NEXT表示・進化一覧）向けのアイコン URL 供給。
 * 生成済みデータURLはキャッシュする。
 */
export class IconProvider {
  private readonly availability: Map<string, boolean>;
  private readonly cache = new Map<string, string>();

  constructor(availability: Map<string, boolean> = new Map()) {
    this.availability = availability;
  }

  hasRealImage(item: StageItemDefinition): boolean {
    return Boolean(item.image) && this.availability.get(item.id) === true;
  }

  /** 実画像があればそのパス、無ければ仮アイコンのデータURLを返す。 */
  iconUrl(item: StageItemDefinition, size = 96): string {
    if (this.hasRealImage(item)) return item.image!;
    const key = `${item.id}:${size}`;
    const cached = this.cache.get(key);
    if (cached) return cached;
    const dataUrl = createIconDataUrl(item, size);
    this.cache.set(key, dataUrl);
    return dataUrl;
  }

  /** 物理オブジェクト用の推奨解像度でアイコンを得る。 */
  gameIconUrl(item: StageItemDefinition): string {
    return this.iconUrl(item, iconRenderSize(item.radius));
  }

  /** プローブ結果を後から上書きする（Phaser のロード失敗時など）。 */
  markUnavailable(itemId: string): void {
    this.availability.set(itemId, false);
    for (const key of [...this.cache.keys()]) {
      if (key.startsWith(`${itemId}:`)) this.cache.delete(key);
    }
  }
}
